import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { yupResolver } from '@hookform/resolvers/yup'
import { PAGE_LOGIN } from 'utils/constants/pages'
import { forgotPasswordSchema } from 'utils/schema/authSchema'
import { Button, TextInput } from 'components/common'
import { ChevronIcon } from 'components/icons'
import { forgotPassword } from 'utils/api/auth'
import { ERROR_CODES } from 'utils/constants'

const { NOT_FOUND, ERR_NETWORK } = ERROR_CODES

type ForgotPasswordFields = {
  email: string
}

const RESET_EMAIL_SENT = 'RESET_EMAIL_SENT'

/**
 * Forgot password form Component
 */
const ForgotPasswordForm = () => {
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFields>({
    resolver: yupResolver(forgotPasswordSchema),
  })

  const requestPasswordChange = async (userData: ForgotPasswordFields) => {
    try {
      const { data } = await forgotPassword(userData)
      if (data.msg === RESET_EMAIL_SENT) {
        toast.success('We have send you an e-mail with an verification link!')
        navigate(PAGE_LOGIN.path)
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        const MESSAGE = error?.response?.data?.errors?.msg
        if (MESSAGE === NOT_FOUND) {
          return toast.error('user does not exist')
        }

        toast.error('Failed to send an e-mail')
      }

      if (error?.code === ERR_NETWORK) {
        return toast.error('Network Error')
      }
    }
  }

  return (
    <section className="w-full max-w-lg rounded-lg p-10">
      <div className="flex flex-col items-center text-center">
        <h1 className="mb-3 text-3xl font-bold">Forgot Password</h1>
        <h3 className="max-w-sm text-center text-sm">
          Forgot your password? No problem. Please enter your email address, and
          we will send you the link!
        </h3>
      </div>
      <div className="mt-5">
        <form onSubmit={handleSubmit(requestPasswordChange)}>
          <TextInput
            required
            register={register}
            name="email"
            label="Email"
            type="text"
            error={errors?.email?.message}
          />
          <Button buttonText="Request New Password" className="mt-5 w-full" />
        </form>
        <Link to={PAGE_LOGIN.path} className="mt-4 flex text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <div className="h-2.5 w-2.5 rotate-90">
              <ChevronIcon />
            </div>
            <div>Back to Login</div>
          </div>
        </Link>
      </div>
    </section>
  )
}

export default ForgotPasswordForm
